/**
 * Copyright 2023-2024 Highway9 Networks Inc.
 */

import { AgentIcon, Badge, Button, Chip, Grid, IconButton, ProfileCard, SxProps, Theme, Tooltip, TopBar } from '@h9/ui-lib';
import React, { useEffect, useMemo, useState, CSSProperties } from "react";
import { useMobile } from "~/hooks/useMobile";
import { authenticationService, userService } from "~/services";
import { Icons } from "~/components/GraphicalResources";
import EditProfile from "./UserProfile/EditProfile";
import ApiTokenDialog from "~/views/adminstration/Tabs/apiToken/ApiTokenDialog";
import { API_DOCS, DOCUMENTATION, IT_DASHBOARD, SUPPORT } from "~/constants/routes";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { selectCurrentServiceController } from "~/store/context/dashboardSlice";
import { authUser } from "~/store/context/authSlice";
import { text } from "~/components/TextResources";
import { useRoles } from "~/hooks/useRoles";
import { utilityActions } from "~/store/context/utilitySlice";
import { useHistory } from "react-router-dom";
import { settingsAssistantEnabled, settingsDashboardInsightsEnabled, settingsSystemDashboardConsoleEnabled } from "~/store/context/settingsSlice";
import NotificationBellIcon from "~/components/Notifications/NotificationBellIcon";
import { getImageSrc } from "~/services/image-service";
import { updateURL } from "~/helpers/history";
import { GoingOutOfSupportChip } from "./GoingOutOfSupportChip";

export type TopBarType = "primary" | "secondary";

export type TopBarPropType = {
  backgroundColor?: CSSProperties["backgroundColor"];
  NotificationComponent?: React.ComponentType;
  subtitle?: string;
  title: string;
  type?: TopBarType;
  bodyId?: string;
};

export function TopBarNew({ subtitle, title, bodyId }: TopBarPropType) {
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [isApiTokenOpen, setIsApiTokenOpen] = React.useState(false);
  const [scrolled, setScrolled] = useState(false);
  const isMobile = useMobile();

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleEditProfileOpen = () => {
    setIsEditOpen(true);
    handleClose();
  };

  const handleEditProfileClose = () => {
    setIsEditOpen(false);
  };

  const currentUserInfo = useAppSelector(authUser) ?? userService.currentUserInfo;
  const { getUserRoleDisplay } = useRoles();
  const provisionedRole = useMemo(() => getUserRoleDisplay(currentUserInfo), [currentUserInfo, getUserRoleDisplay]);
  const [profileImage, setProfileImage] = useState<string | undefined>();
  const profileImageId = currentUserInfo?.profilePicture?.imageId;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!profileImageId) {
        setProfileImage(undefined);
        return;
      }
      const url = await getImageSrc(profileImageId);
      if (!cancelled) setProfileImage(url);
    };
    load();
    return () => { cancelled = true; };
  }, [profileImageId]);
  useEffect(() => {
    if (bodyId) {
      const bodyBlock = document.getElementById(bodyId) as HTMLElement;
      bodyBlock.onscroll = function () {
        if (bodyBlock.scrollTop > 50) {
          setScrolled(true);
        } else {
          setScrolled(false);
        }
      };
    }
  }, [bodyId]);

  return (
    <TopBar
      title={title}
      onMobileSideDrawerOpen={() => dispatch(utilityActions.setMobileSideDrawerOpen(true))}
      subtitle={subtitle}
      userName={authenticationService.currentUserValue?.userName}
      role={provisionedRole}
      profilePicture={profileImage}
      profileCard={
        <ProfileCard
          profile={{
            name: authenticationService.currentUserValue?.userName ?? "",
            role: provisionedRole,
            email: authenticationService.currentUserValue?.email ?? "",
            profilePicture: profileImage ?? '',
          }}
          menuOptions={[
            {
              icon: Icons.edit,
              text: text.editProfile!,
              onClick: handleEditProfileOpen,
            },
            {
              icon: Icons.vpnKey,
              text: "API Token",
              onClick: () => {
                setAnchorEl(null);
                setIsApiTokenOpen(true);
              },
            },
            {
              icon: Icons.code,
              text: text.apiDocs!,
              onClick: () => {
                window.open(API_DOCS, "_blank");
              },
            },
            {
              icon: Icons.documentIcon,
              text: text.documentation!,
              onClick: () => {
                const existingWindow = window.open(DOCUMENTATION, "documentation");
                if (existingWindow) {
                  existingWindow.focus();
                }
              },
            },
            {
              icon: Icons.customerSupportIcon,
              text: text.support!,
              onClick: () => {
                const existingWindow = window.open(SUPPORT, "support");
                if (existingWindow) {
                  existingWindow.focus();
                }
              },
            },
            {
              icon: Icons.logout,
              text: text.logout!,
              style: commonStyles.logoutItem,
              onClick: () => {
                // Monitor > Network Map only: it keeps its node selection in the query string (see
                // NetworkMap.tsx), and logout reloads the current URL. AuthRouter would store that URL -
                // selection included - in sessionStorage("url"), and LoginPage replays it after the next
                // login, showing the previous user's selected radio site. Drop those params before
                // logging out. Every other page keeps its full URL, so the post-login restore is unchanged.
                if (window.location.pathname.replace(/\/$/, "") === IT_DASHBOARD) {
                  updateURL({ selectedNodeID: undefined, selectedNodeType: undefined, leafNodeID: undefined });
                }
                authenticationService.logout();
              },
            },
          ]}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
        />
      }
      setAnchorEl={setAnchorEl}
      style={{ boxShadow: scrolled ? "0 2px 4px 0 rgba(0,0,0,.2);" : null }}
    >
      <Grid sx={commonStyles.rightSpacer} />
      <GoingOutOfSupportChip />
      <VirtualEngineerBtn isMobile={isMobile} />
      <NotificationBellIcon />
      <InsightsIcons />
      {/* {NotificationComponent ? <NotificationComponent /> : <AlertBadgeRegular fontSize={20} />} */}
      <EditProfile state={isEditOpen} handleClose={handleEditProfileClose} />
      <ApiTokenDialog open={isApiTokenOpen} onClose={() => setIsApiTokenOpen(false)} />
    </TopBar>
  );
}

const commonStyles = {
  rightSpacer: {
    flex: 1,
  },
  logoutItem: {
    color: "#D32F2F !important",
    gap: "8px",
    "& .MuiSvgIcon-root": {
      width: "16px",
      height: "16px",
      color: "#DF3D2D",
    },
  },
  tooltip: {
    bgcolor: "#292B2F",
    marginLeft: "8px !important",
    padding: "8px",
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 600,
    color: "#F7F7F7",
  }
} satisfies Record<string, SxProps<Theme>>;

function InsightsIcons() {
  const history = useHistory();

  const dashboardInsightEnabled = useAppSelector(settingsDashboardInsightsEnabled);
  const dashboardConsoleEnabled = useAppSelector(settingsSystemDashboardConsoleEnabled);
  const notificationCount = useAppSelector(selectCurrentServiceController)?.insightsSummary?.activeCount;
  const handleNotificationOpen = () => {
    history.push(IT_DASHBOARD + "?tab=insights");
  };

  if (!dashboardConsoleEnabled) {
    return null;
  }

  if (!dashboardInsightEnabled) {
    return null;
  }

  return (
    <Tooltip title="Insights"
      placement="bottom"
      key="insights"
      aria-label="insights"
      slotProps={
        {
          tooltip: {
            sx: commonStyles.tooltip
          }
        }
      }
    >
    <IconButton onClick={handleNotificationOpen} >
      <Badge badgeContent={notificationCount} color="success">
        <Icons.insights color="primary" />
      </Badge>
    </IconButton>
    </Tooltip>
  );
}

function VirtualEngineerBtn({ isMobile }: { isMobile: boolean }) {
  const dispatch = useAppDispatch();

  const enabled = useAppSelector(settingsAssistantEnabled);

  const handleAIOpen = () => {
    dispatch(utilityActions.toggleChatBot());
  };

  if (!enabled) {
    return null;
  }

  return (
    <Button title="ALT + K / Option + K to open Virtual Engineer"
      sx={{
        height: isMobile ? '1.75rem' : '2rem',
        fontWeight: '600',
        fontSize: isMobile ? '11px' : '13px',
        px: isMobile ? "6px" : "10px",
        minWidth: isMobile ? 'auto' : 'fit-content'
      }}
      startIcon={<AgentIcon color='#226a58' size={isMobile ? 20 : 24} />}
      color="primary"
      variant="outlined"
      onClick={handleAIOpen}>
      {!isMobile && text.askAI} &nbsp;
      {!isMobile && <Chip variant="outlined" size="small" sx={{ mr :2}} color="primary" label={
        <span style={{ display: 'flex', alignItems: 'center' }}>
          AI
          <AutoAwesomeOutlinedIcon
            style={{ fontSize: "small", rotate : '-180deg', marginLeft: '4px' }}
            titleAccess="AutoDelete"
          />
        </span>
      } />}
    </Button>
  );
}
